/*
 * Copyright (C) 2021 DV Bern AG, Switzerland
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as
 * published by the Free Software Foundation, either version 3 of the
 * License, or (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program. If not, see <https://www.gnu.org/licenses/>.
 */

import {
    ChangeDetectionStrategy,
    Component,
    Input,
    OnDestroy,
    OnInit,
    inject
} from '@angular/core';
import {StateService} from '@uirouter/core';
import {TranslateService} from '@ngx-translate/core';
import {Subscription} from 'rxjs';
import {AuthServiceRS} from '../../../../authentication/service/AuthServiceRS.rest';
import {TSWizardStepXTyp} from '../../../../models/enums/TSWizardStepXTyp';
import {TSFerienbetreuungAngabenContainer} from '../../../../models/gemeindeantrag/TSFerienbetreuungAngabenContainer';
import {TSRoleUtil} from '../../../../utils/TSRoleUtil';
import {LogFactory} from '@utils/log';
import {DownloadRS} from '../../../core/service/downloadRS.rest';
import {WizardStepXRS} from '../../../core/service/wizardStepXRS.rest';
import {FerienbetreuungService} from '../services/ferienbetreuung.service';

const LOG = LogFactory.createLog('FerienbetreuungComponent');

@Component({
    selector: 'dv-ferienbetreuung',
    templateUrl: './ferienbetreuung.component.html',
    styleUrls: ['./ferienbetreuung.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class FerienbetreuungComponent implements OnInit, OnDestroy {
    private readonly authServiceRS = inject(AuthServiceRS);
    private readonly ferienbetreuungService = inject(FerienbetreuungService);
    private readonly wizardStepXRS = inject(WizardStepXRS);
    private readonly downloadRS = inject(DownloadRS);
    private readonly translate = inject(TranslateService);
    private readonly stateService = inject(StateService);

    @Input()
    public ferienbetreuungId: string;

    public wizardTyp = TSWizardStepXTyp.FERIENBETREUUNG;
    public ferienbetreuungContainer: TSFerienbetreuungAngabenContainer;

    private subscription: Subscription;

    public ngOnInit(): void {
        this.ferienbetreuungService.updateFerienbetreuungContainerStores(
            this.ferienbetreuungId
        );
        this.subscription = this.ferienbetreuungService
            .getFerienbetreuungContainer()
            .subscribe(
                container => {
                    this.ferienbetreuungContainer = container;
                    // update wizard steps every time LATSAngabenGemeindeContainer is reloaded
                    this.wizardStepXRS.updateSteps(
                        this.wizardTyp,
                        this.ferienbetreuungId
                    );
                },
                err => LOG.error(err)
            );
    }

    public showKommentare(): boolean {
        return this.authServiceRS.isOneOfRoles(TSRoleUtil.getMandantRoles());
    }

    public ngOnDestroy(): void {
        this.subscription.unsubscribe();
        this.ferienbetreuungService.emptyStores();
    }

    public downloadFerienbetreuungReport(): void {
        this.ferienbetreuungService
            .generateFerienbetreuungReport(this.ferienbetreuungContainer)
            .subscribe(
                res => this.openDownloadForFile(res),
                err => LOG.error(err)
            );
    }

    public navigateToVerlauf(): void {
        this.stateService.go('FERIENBETREUUNG.VERLAUF', {
            id: this.ferienbetreuungId
        });
    }

    private openDownloadForFile(response: BlobPart): void {
        const file = new Blob([response], {type: 'application/pdf'});
        const filename = this.translate.instant('FERIENBETREUUNG_REPORT_NAME', {
            gemeinde: this.ferienbetreuungContainer.gemeinde.name,
            gp: this.ferienbetreuungContainer.gesuchsperiode
                .gesuchsperiodeString
        });
        this.downloadRS.openDownload(file, filename);
    }
}
